import {
  DEFAULT_SETTINGS_STATE,
  type SettingsState,
} from "@triedge/staff-settings-domain";
import type {
  IntellioAssistantWorkflowDefinitionRecord,
  IntellioAssistantWorkflowExecutionRecord,
  IntellioAssistantWorkflowMutationResult,
  IntellioAssistantWorkflowStudioStateOutput,
  IntellioAssistantWorkflowTemplateRecord,
  IntellioMutateAssistantRunOutput,
} from "@triedge/domain-core/contracts";
import type {
  AssistantWorkflowStudioAdapter,
  AssistantSpreadsheetBridgeAdapter,
  AssistantSpreadsheetBridgeResponse,
  AssistantSpreadsheetBridgeReturnContext,
  FinancialAidHistoricalImportAdapter,
  ModuleConfigResponse,
  PaymentControlPlaneAdapter,
  PaymentControlPlaneReadiness,
  SettingsConfigAdapter,
  UiConsultationAdapter,
  UiConsultationRunRecord,
  UiConsultationServiceStatus,
  UiConsultationSessionRecord,
} from "../types";
import { applyDomainPatch, cloneValue } from "../utils/settingsState";

const STORAGE_KEYS = {
  returnContext: "staff-settings-ui.mock.returnContext",
  completionContext: "staff-settings-ui.mock.completionContext",
};

const canUseStorage = (): boolean =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const readStoredJson = <T>(key: string): T | null => {
  if (!canUseStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (key: string, value: unknown): void => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(value));
};

const removeStoredJson = (key: string): void => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.removeItem(key);
};

const readText = (...values: unknown[]): string => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) {
      return text;
    }
  }
  return "";
};

const buildExportError = (code: string, message: string): Error => {
  const error = new Error(`${code}: ${message}`);
  (error as Error & { code?: string }).code = code;
  return error;
};

const decodeDataUrl = (
  value: unknown,
): { mimeType: string; text: string } | null => {
  const sourceUrl = readText(value);
  if (!sourceUrl) {
    return null;
  }
  const match = /^data:([^,]*),(.*)$/is.exec(sourceUrl);
  if (!match) {
    throw buildExportError(
      "assistant_spreadsheet_export_unsupported_source",
      "artifactDownloadUrl must be a data URL for the local Google Sheets bridge.",
    );
  }
  const metadata = match[1] ?? "";
  const encodedPayload = match[2] ?? "";
  const mimeType = readText(metadata.split(";")[0]);
  if (metadata.toLowerCase().split(";").includes("base64")) {
    const binary = globalThis.atob(encodedPayload);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return {
      mimeType,
      text: new TextDecoder().decode(bytes),
    };
  }
  try {
    return {
      mimeType,
      text: decodeURIComponent(encodedPayload.replace(/\+/g, "%20")),
    };
  } catch {
    return {
      mimeType,
      text: encodedPayload,
    };
  }
};

const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (inQuotes) {
      if (char === '"' && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ",") {
      row.push(cell);
      cell = "";
      continue;
    }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && text[index + 1] === "\n") {
        index += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }
  if (cell || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows
    .map((entries) => entries.map((entry) => entry.trim()))
    .filter((entries) => entries.some(Boolean));
};

const decodeXmlEntities = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const parseSpreadsheetXmlRows = (text: string): string[][] => {
  const rows: string[][] = [];
  const rowRegex = /<Row\b[\s\S]*?<\/Row>/gi;
  for (const rowMatch of text.matchAll(rowRegex)) {
    const rowXml = rowMatch[0];
    const cells: string[] = [];
    const cellRegex = /<Cell\b[^>]*>([\s\S]*?)<\/Cell>/gi;
    for (const cellMatch of rowXml.matchAll(cellRegex)) {
      const dataMatch = /<Data\b[^>]*>([\s\S]*?)<\/Data>/i.exec(
        cellMatch[1] ?? "",
      );
      const rawValue = (dataMatch?.[1] ?? "").replace(/<[^>]+>/g, "").trim();
      cells.push(decodeXmlEntities(rawValue));
    }
    if (cells.some(Boolean)) {
      rows.push(cells);
    }
  }
  return rows;
};

const normalizeSpreadsheetRows = (
  rows: string[][],
  sourceKind: "csv_data_url" | "spreadsheetml_data_url",
) => {
  const [headerRow, ...dataRows] = rows;
  const headers = (headerRow ?? []).map(
    (header, index) => header || `Column ${index + 1}`,
  );
  const normalizedRows = dataRows.filter((row) =>
    row.some((cell) => readText(cell)),
  );
  if (headers.length === 0 || normalizedRows.length === 0) {
    throw buildExportError(
      "assistant_spreadsheet_export_empty",
      `${sourceKind} did not contain data rows to export.`,
    );
  }
  return {
    sourceKind,
    rowCount: normalizedRows.length,
    columnCount: headers.length,
    headers,
    previewRows: normalizedRows
      .slice(0, 5)
      .map((row) =>
        Object.fromEntries(
          headers.map((header, index) => [header, row[index] ?? ""]),
        ),
      ),
  };
};

const parseAssistantSpreadsheetExportSource = (input: {
  artifactDownloadUrl?: string | null;
  mimeType?: string | null;
}) => {
  const decoded = decodeDataUrl(input.artifactDownloadUrl);
  if (!decoded) {
    return null;
  }
  const mimeType = readText(input.mimeType, decoded.mimeType);
  const isSpreadsheetXml =
    /spreadsheetml|application\/vnd\.ms-excel|\.xls\b/i.test(mimeType) ||
    /<Workbook\b|<ss:Workbook\b|<Row\b/i.test(decoded.text);
  return normalizeSpreadsheetRows(
    isSpreadsheetXml
      ? parseSpreadsheetXmlRows(decoded.text)
      : parseCsvRows(decoded.text),
    isSpreadsheetXml ? "spreadsheetml_data_url" : "csv_data_url",
  );
};

type PaymentMethodConfigurations = NonNullable<
  PaymentControlPlaneReadiness["paymentMethodConfigurations"]
>;
type PaymentMethodConfigurationAssignmentSeed = {
  operationKey: PaymentMethodConfigurations["effectiveAssignments"][number]["operationKey"];
  catalogEntryId: string | null;
};

const DEFAULT_PLATFORM_PAYMENT_METHOD_CONFIGURATION_CATALOG: PaymentMethodConfigurations["platformCatalog"] =
  [
    {
      id: "platform-stripe-default",
      label: "Stripe default",
      paymentMethodConfigurationId: null,
      enabled: true,
      source: "platform",
    },
    {
      id: "platform-card-and-ach",
      label: "Card and ACH",
      paymentMethodConfigurationId: "pmc_platform_card_ach",
      enabled: true,
      source: "platform",
    },
  ];

const DEFAULT_SCHOOL_PAYMENT_METHOD_CONFIGURATION_CATALOG: PaymentMethodConfigurations["schoolCatalog"] =
  [
    {
      id: "school-card-only",
      label: "School card only",
      paymentMethodConfigurationId: "pmc_storybook_card_only",
      enabled: true,
      source: "school",
    },
  ];

const DEFAULT_PAYMENT_METHOD_CONFIGURATION_ASSIGNMENTS: PaymentMethodConfigurationAssignmentSeed[] =
  [
    { operationKey: "tuition_checkout", catalogEntryId: "school-card-only" },
    { operationKey: "enrollment_registration_checkout", catalogEntryId: null },
    { operationKey: "donations_checkout_one_time", catalogEntryId: null },
    { operationKey: "donations_checkout_recurring", catalogEntryId: null },
    { operationKey: "donations_parnes_hayom_checkout", catalogEntryId: null },
    { operationKey: "tip_a_teacher_checkout", catalogEntryId: null },
    { operationKey: "camps_registration_checkout", catalogEntryId: null },
    { operationKey: "camps_balance_checkout", catalogEntryId: null },
  ];

const buildPaymentMethodConfigurations = (
  schoolCatalog: PaymentMethodConfigurations["schoolCatalog"] = DEFAULT_SCHOOL_PAYMENT_METHOD_CONFIGURATION_CATALOG,
  assignments: PaymentMethodConfigurationAssignmentSeed[] = DEFAULT_PAYMENT_METHOD_CONFIGURATION_ASSIGNMENTS,
): PaymentMethodConfigurations => {
  const platformCatalog = cloneValue(
    DEFAULT_PLATFORM_PAYMENT_METHOD_CONFIGURATION_CATALOG,
  );
  const schoolPaymentMethodConfigurationCatalog = cloneValue(schoolCatalog);
  const effectiveCatalog: PaymentMethodConfigurations["effectiveCatalog"] = [
    ...platformCatalog,
    ...schoolPaymentMethodConfigurationCatalog,
  ];
  const catalogById = new Map(
    effectiveCatalog.map((entry) => [entry.id, entry]),
  );

  return {
    platformCatalog,
    schoolCatalog: schoolPaymentMethodConfigurationCatalog,
    effectiveCatalog,
    effectiveAssignments: cloneValue(assignments).map((assignment) => ({
      ...assignment,
      resolvedCatalogEntry: assignment.catalogEntryId
        ? cloneValue(catalogById.get(assignment.catalogEntryId) ?? null)
        : null,
    })),
    issues: [],
  };
};

const assignmentsFromPaymentMethodConfigurations = (
  paymentMethodConfigurations: PaymentMethodConfigurations,
): PaymentMethodConfigurationAssignmentSeed[] =>
  paymentMethodConfigurations.effectiveAssignments.map((assignment) => ({
    operationKey: assignment.operationKey,
    catalogEntryId: assignment.catalogEntryId,
  }));

const createDefaultPaymentReadiness = (): PaymentControlPlaneReadiness => ({
  status: "test_ready",
  blockingIssueCount: 0,
  warningCount: 1,
  issues: [
    {
      code: "storybook-seeded",
      message:
        "Using seeded Stripe control-plane state for package preview and demo consumption.",
      severity: "info",
    },
  ],
  controlPlane: {
    onboardingStatus: "pending",
    chargeStrategy: "direct",
    defaultStripeAccountId: "acct_storybook_seeded",
    hasModuleOverrides: false,
    updatedAt: "2026-03-16T09:30:00.000Z",
    updatedBy: "storybook-seeded",
  },
  runtime: {
    stripeSecretMode: "test",
    hasStripeSecretConfigured: true,
    hasWebhookSecretConfigured: true,
  },
  publicTuition: {
    savedMethodsEnabled: false,
    autoPayEnabled: false,
    autoPaySweepKillSwitch: true,
    webhookReconciliationReady: false,
    transactionalEmailReady: false,
    launchQaApproved: false,
    paymentMethodPolicy: {
      automaticPaymentMethodsEnabled: true,
      allowedPaymentMethodTypes: ["card"],
    },
  },
  urls: {
    successUrl: "https://example.com/success",
    cancelUrl: "https://example.com/cancel",
    portalReturnUrl: "https://example.com/portal",
    webhookUrl: "https://example.com/webhook",
    manifestUrl: "https://example.com/manifest.json",
  },
  stripeHostedReadiness: {
    runtime: {
      stripeSecretMode: "test",
      hasStripeSecretConfigured: true,
      hasWebhookSecretConfigured: true,
      hasSuccessUrlConfigured: true,
      hasCancelUrlConfigured: true,
      hasPortalReturnUrlConfigured: true,
      hasWebhookUrlConfigured: true,
    },
    account: {
      exists: true,
      livemode: false,
      chargesEnabled: true,
      payoutsEnabled: false,
      detailsSubmitted: true,
      displayName: "Storybook Test Account",
      email: "storybook@example.com",
      country: "US",
      errorMessage: null,
    },
  },
  solaProviderReadiness: {
    enabled: true,
    donationsEnabled: true,
    publicIfieldsKey: "ifields_storybook_seeded",
    hasPublicIfieldsKeyConfigured: true,
    hasTransactionKeyConfigured: true,
    hasWebhookConfigured: false,
    terminalEnabled: false,
  },
  donationsFundingReadiness: {
    oneTimePrimaryFundingMethod: "sola_daf",
    oneTimeFallbackFundingMethod: "stripe_card",
    recurringFundingMethod: "stripe_card",
    oneTimeResolvedFundingMethod: "sola_daf",
    recurringResolvedFundingMethod: "stripe_card",
    availableFundingMethods: [
      {
        method: "sola_daf",
        label: "Sola DAF",
        available: true,
        reasonCode: null,
        reasonMessage: null,
      },
      {
        method: "stripe_card",
        label: "Stripe card",
        available: true,
        reasonCode: null,
        reasonMessage: null,
      },
    ],
  },
  sola: {
    enabled: true,
    donationsEnabled: true,
    publicIfieldsKey: "ifields_storybook_seeded",
    hasPublicIfieldsKeyConfigured: true,
    hasTransactionKeyConfigured: true,
    hasWebhookConfigured: false,
    primaryDonationFundingMethod: "sola_daf",
    fallbackDonationFundingMethod: "stripe_card",
    terminalEnabled: false,
  },
  banquest: {
    enabled: false,
    registrationMode: "manual",
    transactionRetrievalMode: "curated_import_only",
    webhookId: null,
    webhookPathKey: null,
    webhookActive: false,
    lastWebhookSyncAt: null,
    allowedImportModules: [
      "donations",
      "tuition",
      "tip-a-teacher",
      "camps",
      "enrollment",
    ],
    hasSourceKeyConfigured: false,
    hasSourceKeyPinConfigured: false,
    hasApiCredentialConfigured: false,
    hasWebhookSignatureConfigured: false,
    environmentKind: "sandbox",
    hasTokenizationPublicKeyConfigured: false,
    tokenizationScriptUrl: null,
    webhookUrl: null,
    apiBaseUrl: "https://api.banquestgateway.com/api/v2",
  },
  stripeAccount: {
    exists: true,
    livemode: false,
    chargesEnabled: true,
    payoutsEnabled: false,
    detailsSubmitted: true,
    displayName: "Storybook Test Account",
    email: "storybook@example.com",
    country: "US",
    errorMessage: null,
  },
  paymentMethodConfigurations: buildPaymentMethodConfigurations(),
  latestMirrorEvent: {
    eventType: "account.updated",
    moduleTarget: "payments-school-control-plane",
    occurredAt: "2026-03-16T09:15:00.000Z",
    recordedAt: "2026-03-16T09:15:03.000Z",
    providerEventId: "evt_storybook_seeded",
  },
});

type MockSettingsAdapterState = {
  settingsState: SettingsState;
  paymentReadiness: PaymentControlPlaneReadiness;
  assistantConnected: boolean;
  assistantPresets: Array<Record<string, unknown>>;
  assistantUpdatedAt: string;
  workflowRecord: IntellioAssistantWorkflowDefinitionRecord | null;
  workflowExecutions: IntellioAssistantWorkflowExecutionRecord[];
  historicalImportStatus: {
    status: string;
    importId: string | null;
    templateVersion: string | null;
    lastFileName: string | null;
    lastS3Key: string | null;
    lastImportedAt: string | null;
    processedRows: number;
    acceptedRows: number;
    rejectedRows: number;
    errorSummary: string | null;
  };
  uiConsultationSessions: UiConsultationSessionRecord[];
  uiConsultationRuns: UiConsultationRunRecord[];
  uiConsultationStatus: UiConsultationServiceStatus;
};

const createMockState = (): MockSettingsAdapterState => ({
  settingsState: cloneValue(DEFAULT_SETTINGS_STATE),
  paymentReadiness: createDefaultPaymentReadiness(),
  assistantConnected: false,
  assistantPresets: [],
  assistantUpdatedAt: "2026-03-16T09:00:00.000Z",
  workflowRecord: null,
  workflowExecutions: [],
  historicalImportStatus: {
    status: "idle",
    importId: null,
    templateVersion: "storybook-template-v1",
    lastFileName: null,
    lastS3Key: null,
    lastImportedAt: null,
    processedRows: 0,
    acceptedRows: 0,
    rejectedRows: 0,
    errorSummary: null,
  },
  uiConsultationSessions: [],
  uiConsultationRuns: [],
  uiConsultationStatus: {
    visibility: "hidden",
    availability: "disabled",
    message:
      "UI consultation is hidden in the default shared package mock adapter.",
    configuredModelLabel: "Claude Opus 4.6",
    configuredModelId: "us.anthropic.claude-opus-4-6-v1",
    latestRunId: null,
  },
});

const asUnixTimestamp = (value: number) => value as never;

const DEFAULT_TEMPLATE: IntellioAssistantWorkflowTemplateRecord = {
  id: "admissions.dataset_to_spreadsheet_delivery",
  title: "Admissions Dataset to Spreadsheet",
  summary: "Build and deliver an admissions spreadsheet export.",
  module: "admissions",
  routeScope: "/school/admissions",
  curatorOwned: true,
  supportedTriggers: ["manual", "schedule"],
  supportedConnectors: ["email", "google_drive"],
  nodeCatalog: [
    "manual",
    "dataset",
    "filter",
    "select",
    "spreadsheet",
    "email",
    "google_drive",
  ],
  defaultGraphSpec: {
    version: "v1",
    nodes: [
      { id: "trigger-manual", kind: "manual", label: "Run manually" },
      {
        id: "dataset-applications",
        kind: "dataset",
        label: "Admissions dataset",
      },
      {
        id: "spreadsheet-output",
        kind: "spreadsheet",
        label: "Build spreadsheet",
      },
    ],
    edges: [
      {
        id: "e1",
        source: "trigger-manual",
        target: "dataset-applications",
        label: null,
      },
      {
        id: "e2",
        source: "dataset-applications",
        target: "spreadsheet-output",
        label: null,
      },
    ],
  },
  mermaidSpec:
    "flowchart LR; trigger-manual-->dataset-applications-->spreadsheet-output;",
  requiresModeration: true,
  allowsAutonomousDelivery: true,
  createdAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
  updatedAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
};

const toModuleConfigResponse = (input: {
  orgId?: string | null;
  moduleKey?: string | null;
  payload: SettingsState;
}): ModuleConfigResponse => ({
  orgId: (input.orgId ?? "default") as ModuleConfigResponse["orgId"],
  moduleKey: (input.moduleKey ??
    "staff_settings") as ModuleConfigResponse["moduleKey"],
  payload: JSON.stringify(input.payload) as ModuleConfigResponse["payload"],
  version: 1,
  updatedAt: Date.now() as ModuleConfigResponse["updatedAt"],
  updatedBy: "storybook-seeded" as ModuleConfigResponse["updatedBy"],
  integrationStatus: {
    state: "connected",
  } as ModuleConfigResponse["integrationStatus"],
});

const createMockId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const toStaffDirectoryResponse = (
  state: SettingsState,
  input: {
    search?: string;
    statusFilter?: "all" | "active" | "invited" | "deactivated";
    page?: number;
    limit?: number;
  } = {},
) => {
  const needle = String(input.search ?? "")
    .trim()
    .toLowerCase();
  const statusFilter = input.statusFilter ?? "all";
  const page = Math.max(1, Number(input.page ?? 1));
  const limit = Math.min(100, Math.max(1, Number(input.limit ?? 20)));
  const filtered = state.staff.filter((entry) => {
    if (statusFilter !== "all" && entry.status !== statusFilter) {
      return false;
    }
    if (!needle) {
      return true;
    }
    return `${entry.firstName} ${entry.lastName} ${entry.email} ${entry.role}`
      .toLowerCase()
      .includes(needle);
  });
  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * limit;
  return {
    items: filtered.slice(start, start + limit).map((entry) => {
      const ext = (entry.ext ?? {}) as Record<string, unknown>;
      return {
        userId: entry.id,
        firstName: entry.firstName,
        lastName: entry.lastName,
        email: entry.email,
        role: entry.role,
        status: entry.status,
        lastActiveAt: entry.lastActive ?? null,
        invitedAt: String(ext.invitedAt ?? "") || null,
        deactivatedAt: String(ext.deactivatedAt ?? "") || null,
        activationUrl:
          entry.status === "invited"
            ? `https://example.com/activate/${entry.id}`
            : null,
      };
    }),
    totalCount,
    page: safePage,
    totalPages,
  };
};

export const createMockSettingsAdapters = (routeBase = "/school/settings") => {
  const state = createMockState();

  const settingsConfigAdapter: SettingsConfigAdapter = {
    async getModuleConfig(input = {}) {
      return toModuleConfigResponse({
        orgId: input.orgId,
        moduleKey: input.moduleKey,
        payload: cloneValue(state.settingsState),
      });
    },
    async updateModuleConfig(input) {
      state.settingsState = applyDomainPatch(
        cloneValue(state.settingsState) as unknown as Record<string, unknown>,
        cloneValue(input.patch) as Record<string, unknown>,
      ) as unknown as SettingsState;
      return toModuleConfigResponse({
        orgId: input.orgId,
        moduleKey: input.moduleKey,
        payload: cloneValue(state.settingsState),
      });
    },
    async listStaffUsers(input = {}) {
      return toStaffDirectoryResponse(cloneValue(state.settingsState), input);
    },
    async inviteStaffUser(input) {
      const firstName = String(input.firstName ?? "").trim();
      const lastName = String(input.lastName ?? "").trim();
      const email = String(input.email).trim().toLowerCase();
      const existingStaff = state.settingsState.staff.find(
        (entry) =>
          String(entry.email ?? "")
            .trim()
            .toLowerCase() === email,
      );
      if (existingStaff) {
        if (existingStaff.status === "active") {
          throw new Error("A staff user with this email is already active.");
        }
        if (existingStaff.status === "deactivated") {
          throw new Error(
            "A staff user with this email is deactivated. Reactivate the staff user before sending a new invite.",
          );
        }
        if (existingStaff.status !== "invited") {
          throw new Error("A staff user with this email already exists.");
        }
        const resentAt = new Date().toISOString();
        const ext =
          existingStaff.ext &&
          typeof existingStaff.ext === "object" &&
          !Array.isArray(existingStaff.ext)
            ? (existingStaff.ext as Record<string, unknown>)
            : {};
        return {
          userId: existingStaff.id,
          activationUrl: `https://example.com/activate/${existingStaff.id}`,
          emailQueued: true,
          invitedAt: String(ext.invitedAt ?? resentAt),
        };
      }
      const nextUserId = createMockId("staff");
      const invitedAt = new Date().toISOString();
      state.settingsState.staff = [
        {
          id: nextUserId,
          firstName,
          lastName,
          email,
          role: input.role,
          status: "invited",
          lastActive: undefined,
          ext: {
            invitedAt,
          },
        },
        ...state.settingsState.staff,
      ];
      return {
        userId: nextUserId,
        activationUrl: `https://example.com/activate/${nextUserId}`,
        emailQueued: false,
        invitedAt,
      };
    },
    async resendStaffInvite(input) {
      const staff = state.settingsState.staff.find(
        (entry) => entry.id === input.userId,
      );
      if (!staff || staff.status !== "invited") {
        throw new Error("Staff invite could not be found.");
      }
      const resentAt = new Date().toISOString();
      const ext =
        staff.ext && typeof staff.ext === "object" && !Array.isArray(staff.ext)
          ? (staff.ext as Record<string, unknown>)
          : {};
      return {
        userId: staff.id,
        activationUrl: `https://example.com/activate/${staff.id}`,
        emailQueued: true,
        invitedAt: String(ext.invitedAt ?? resentAt),
        resentAt,
      };
    },
    async updateStaffUserRole(input) {
      state.settingsState.staff = state.settingsState.staff.map((entry) =>
        entry.id === input.userId ? { ...entry, role: input.role } : entry,
      );
      return {
        userId: input.userId,
        updatedAt: new Date().toISOString(),
        role: input.role,
      };
    },
    async deactivateStaffUser(input) {
      const deactivatedAt = new Date().toISOString();
      state.settingsState.staff = state.settingsState.staff.map((entry) =>
        entry.id === input.userId
          ? {
              ...entry,
              status: "deactivated",
              ext: {
                ...(entry.ext ?? {}),
                deactivatedAt,
              },
            }
          : entry,
      );
      return {
        userId: input.userId,
        deactivatedAt,
      };
    },
    async reactivateStaffUser(input) {
      const reactivatedAt = new Date().toISOString();
      state.settingsState.staff = state.settingsState.staff.map((entry) =>
        entry.id === input.userId
          ? {
              ...entry,
              status: "active",
              lastActive: reactivatedAt,
              ext: {
                ...(entry.ext ?? {}),
                deactivatedAt: null,
              },
            }
          : entry,
      );
      return {
        userId: input.userId,
        reactivatedAt,
      };
    },
    async listStaffCodes() {
      return {
        items: cloneValue(
          state.settingsState.staffProvisioning.staffSignupCodes,
        ).map((entry) => ({
          codeId: entry.codeId,
          label: entry.label,
          maskedValue: entry.maskedValue,
          status: entry.status,
          createdAt: entry.createdAt,
        })),
      };
    },
    async createStaffCode(input) {
      const codeId = createMockId("staff-code");
      const fullValue = `YST-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
      const createdAt = new Date().toISOString();
      state.settingsState.staffProvisioning.staffSignupCodes = [
        {
          codeId,
          label: input.label,
          maskedValue: `••••${fullValue.slice(-4)}`,
          codeHash: `hash:${codeId}`,
          salt: `salt:${codeId}`,
          status: "active",
          createdAt,
          createdBy: "storybook-seeded",
          rotatedAt: null,
          rotatedBy: null,
          revokedAt: null,
          revokedBy: null,
        },
        ...state.settingsState.staffProvisioning.staffSignupCodes,
      ];
      return {
        codeId,
        label: input.label,
        maskedValue: `••••${fullValue.slice(-4)}`,
        status: "active",
        createdAt,
        fullValue,
      };
    },
    async rotateStaffCode(input) {
      const fullValue = `YST-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
      const rotatedAt = new Date().toISOString();
      let nextLabel = "Staff Signup";
      let nextMaskedValue = `••••${fullValue.slice(-4)}`;
      state.settingsState.staffProvisioning.staffSignupCodes =
        state.settingsState.staffProvisioning.staffSignupCodes.map((entry) => {
          if (entry.codeId !== input.codeId) {
            return entry;
          }
          nextLabel = entry.label;
          nextMaskedValue = `••••${fullValue.slice(-4)}`;
          return {
            ...entry,
            maskedValue: nextMaskedValue,
            rotatedAt,
            rotatedBy: "storybook-seeded",
            revokedAt: null,
            revokedBy: null,
            status: "active",
          };
        });
      return {
        codeId: input.codeId,
        label: nextLabel,
        maskedValue: nextMaskedValue,
        status: "active",
        createdAt: rotatedAt,
        fullValue,
        rotatedAt,
      };
    },
    async revokeStaffCode(input) {
      const revokedAt = new Date().toISOString();
      state.settingsState.staffProvisioning.staffSignupCodes =
        state.settingsState.staffProvisioning.staffSignupCodes.map((entry) =>
          entry.codeId === input.codeId
            ? {
                ...entry,
                status: "revoked",
                revokedAt,
                revokedBy: "storybook-seeded",
              }
            : entry,
        );
      return {
        codeId: input.codeId,
        revokedAt,
      };
    },
    async listApprovedDomains() {
      return {
        items: cloneValue(
          state.settingsState.staffProvisioning.approvedDomains,
        ),
      };
    },
    async addApprovedDomain(input) {
      const normalizedDomain = String(input.domain).trim().toLowerCase();
      const existing =
        state.settingsState.staffProvisioning.approvedDomains.find(
          (entry) => entry.domain === normalizedDomain,
        );
      if (existing) {
        return cloneValue(existing);
      }
      const created = {
        domain: normalizedDomain,
        addedAt: new Date().toISOString(),
        addedBy: "storybook-seeded",
      };
      state.settingsState.staffProvisioning.approvedDomains = [
        ...state.settingsState.staffProvisioning.approvedDomains,
        created,
      ];
      return created;
    },
    async removeApprovedDomain(input) {
      const normalizedDomain = String(input.domain).trim().toLowerCase();
      const existing =
        state.settingsState.staffProvisioning.approvedDomains.find(
          (entry) => entry.domain === normalizedDomain,
        ) ?? {
          domain: normalizedDomain,
          addedAt: new Date().toISOString(),
          addedBy: "storybook-seeded",
        };
      state.settingsState.staffProvisioning.approvedDomains =
        state.settingsState.staffProvisioning.approvedDomains.filter(
          (entry) => entry.domain !== normalizedDomain,
        );
      return cloneValue(existing);
    },
    async listModuleEnablementAuditEvents(input = {}) {
      const events = Array.isArray(
        (state.settingsState.organization.ext as Record<string, unknown>)
          ?.moduleGovernanceAuditEvents,
      )
        ? cloneValue(
            (state.settingsState.organization.ext as Record<string, unknown>)
              .moduleGovernanceAuditEvents as Array<Record<string, unknown>>,
          )
        : [];
      const limit = Math.max(1, Number(input.limit ?? 5));
      return {
        events: events.slice(0, limit).map((entry, index) => ({
          eventId: String(entry.eventId ?? createMockId(`audit-${index + 1}`)),
          actorEmail: String(
            entry.actorEmail ?? "storybook-seeded@intellio.local",
          ),
          actorRole: (String(entry.actorRole ?? "super-administrator") ||
            "super-administrator") as "admin" | "owner" | "super-administrator",
          moduleId: String(entry.moduleId ?? "settings"),
          moduleLabel: String(entry.moduleLabel ?? "Settings"),
          action: (String(entry.action ?? "enabled") || "enabled") as
            | "enabled"
            | "disabled"
            | "route_group_enabled"
            | "route_group_disabled",
          occurredAt: String(entry.occurredAt ?? new Date().toISOString()),
          routeGroupId: String(entry.routeGroupId ?? "") || null,
          routeGroupLabel: String(entry.routeGroupLabel ?? "") || null,
        })),
      };
    },
    async listNotifications() {
      return [];
    },
    async listNotificationPreferences() {
      return [];
    },
    async updateNotificationPreferences(input) {
      return cloneValue(input);
    },
  };

  const assistantSpreadsheetBridgeAdapter: AssistantSpreadsheetBridgeAdapter = {
    surfaceRoute: `${routeBase}/integrations`,
    async getStatus() {
      const payload: AssistantSpreadsheetBridgeResponse = {
        ok: true,
        status: state.assistantConnected ? "connected" : "not_connected",
        targetRoute: `${routeBase}/integrations`,
        payload: {
          connected: state.assistantConnected,
          supportedEnvironment: true,
          bridgeState: state.assistantConnected ? "connected" : "not_connected",
          expiryState: state.assistantConnected ? "active" : "missing",
          updatedAt: state.assistantUpdatedAt,
          presets: cloneValue(state.assistantPresets),
        },
      };
      return payload;
    },
    async startAuth(input) {
      return {
        ok: true,
        status: "auth_started",
        authorizationUrl:
          "https://accounts.google.com/o/oauth2/v2/auth?client_id=storybook",
        redirectUri:
          input.redirectUri ??
          this.buildCallbackUri({ returnTo: `${routeBase}/integrations` }),
        targetRoute: `${routeBase}/integrations`,
      };
    },
    async completeAuth(input) {
      state.assistantConnected = true;
      state.assistantUpdatedAt = new Date().toISOString();
      return {
        ok: true,
        status: "connected",
        message: "Mock OAuth callback completed.",
        payload: {
          state: input.state,
          code: input.code,
        },
        targetRoute: `${routeBase}/integrations`,
      };
    },
    async exportToDrive(input) {
      const workbookArtifactPath = readText(input.workbookArtifactPath);
      const artifactDownloadUrl = readText(input.artifactDownloadUrl);
      const fileName = readText(input.fileName);
      if (!fileName) {
        throw buildExportError(
          "assistant_spreadsheet_export_invalid_request",
          "fileName is required.",
        );
      }
      if (!workbookArtifactPath && !artifactDownloadUrl) {
        throw buildExportError(
          "assistant_spreadsheet_export_invalid_request",
          "workbookArtifactPath or artifactDownloadUrl is required.",
        );
      }
      const shouldParseDownloadUrl =
        Boolean(artifactDownloadUrl) &&
        (!workbookArtifactPath ||
          /text\/csv|application\/vnd\.ms-excel|spreadsheetml|\bcsv\b|\bxls\b/i.test(
            `${input.mimeType ?? ""} ${artifactDownloadUrl}`,
          ));
      const parsedSource = shouldParseDownloadUrl
        ? parseAssistantSpreadsheetExportSource({
            artifactDownloadUrl,
            mimeType: input.mimeType,
          })
        : null;
      const sourceKind = workbookArtifactPath
        ? "workbook_artifact_path"
        : (parsedSource?.sourceKind ?? "artifact_download_url");
      const createdAt = new Date().toISOString();
      const expiresAt = new Date(createdAt).getTime() + 24 * 60 * 60 * 1000;
      return {
        ok: true,
        status: "export_completed",
        message:
          "Temporary Intellio-owned Google Sheet created. Make your own copy in Google Sheets to keep it.",
        exportId: `storybook-export-${Date.now()}`,
        retentionHours: 24,
        expiresAt: new Date(expiresAt).toISOString(),
        fileName,
        fileUrl: new URL(
          "/spreadsheets/d/storybook-file-id/edit",
          "https://docs.google.com",
        ).toString(),
        payload: {
          workbookArtifactPath: workbookArtifactPath || null,
          artifactDownloadUrl: artifactDownloadUrl || null,
          mimeType: readText(input.mimeType) || null,
          sourceKind,
          rowCount: parsedSource?.rowCount ?? null,
          columnCount: parsedSource?.columnCount ?? null,
          headers: parsedSource?.headers ?? [],
          previewRows: parsedSource?.previewRows ?? [],
        },
      };
    },
    async disconnect() {
      state.assistantConnected = false;
      state.assistantUpdatedAt = new Date().toISOString();
      return {
        ok: true,
        status: "disconnected",
      };
    },
    async savePreset(input) {
      const presetId = String(
        input.presetId ?? `preset-${state.assistantPresets.length + 1}`,
      );
      const nextPreset = {
        presetId,
        label: String(input.label ?? "Preset"),
        destinationType: String(input.destinationType ?? "new_sheet"),
        destinationFileId: input.destinationFileId ?? null,
        destinationFolderId: input.destinationFolderId ?? null,
        spreadsheetUrl: input.spreadsheetUrl ?? null,
        fileNameTemplate: input.fileNameTemplate ?? null,
      };
      state.assistantPresets = [
        ...state.assistantPresets.filter(
          (entry) => String(entry.presetId) !== presetId,
        ),
        nextPreset,
      ];
      return {
        ok: true,
        status: "saved",
        payload: nextPreset,
      };
    },
    async deletePreset(input) {
      const presetId = String(input.presetId ?? "");
      state.assistantPresets = state.assistantPresets.filter(
        (entry) => String(entry.presetId) !== presetId,
      );
      return {
        ok: true,
        status: "deleted",
      };
    },
    buildCallbackUri() {
      if (typeof window !== "undefined") {
        return `${window.location.origin}/auth?bridgeCallback=assistant-spreadsheet`;
      }
      return "https://example.com/auth?bridgeCallback=assistant-spreadsheet";
    },
    isRedirectUriAllowed() {
      return true;
    },
    getReturnContext() {
      return readStoredJson<AssistantSpreadsheetBridgeReturnContext>(
        STORAGE_KEYS.returnContext,
      );
    },
    storeReturnContext(context) {
      writeStoredJson(STORAGE_KEYS.returnContext, context);
    },
    clearReturnContext() {
      removeStoredJson(STORAGE_KEYS.returnContext);
    },
    storeCompletionContext(context) {
      writeStoredJson(STORAGE_KEYS.completionContext, context);
    },
  };

  const buildWorkflowStudioState = (
    workflowId: string,
  ): IntellioAssistantWorkflowStudioStateOutput => ({
    mode: workflowId === "new" ? "new" : "existing",
    workflowId,
    workflow: workflowId === "new" ? null : cloneValue(state.workflowRecord),
    templates: [cloneValue(DEFAULT_TEMPLATE)],
    executions: cloneValue(state.workflowExecutions),
    executionsNextToken: null,
    viewerAuthority: {
      actorId: "storybook-staff",
      actorRole: "staff",
      canEdit: true,
      canReview: true,
      canApprove: true,
      canRun: workflowId !== "new" && Boolean(state.workflowRecord),
    },
    connectorReadiness: {
      email: {
        status: "ready",
        connected: true,
        ready: true,
        grantedScopes: [],
        requiredScopes: [],
        missingScopes: [],
        lastVerifiedAt: null,
        message: "Email delivery is available in Storybook preview.",
      },
      googleWorkspace: {
        status: state.assistantConnected ? "ready" : "not_connected",
        connected: state.assistantConnected,
        ready: state.assistantConnected,
        grantedScopes: state.assistantConnected
          ? ["https://www.googleapis.com/auth/drive.file"]
          : [],
        requiredScopes: ["https://www.googleapis.com/auth/drive.file"],
        missingScopes: state.assistantConnected
          ? []
          : ["https://www.googleapis.com/auth/drive.file"],
        lastVerifiedAt: state.assistantConnected
          ? asUnixTimestamp(Math.floor(Date.now() / 1000))
          : null,
        message: state.assistantConnected
          ? "Google Workspace is connected in Storybook preview."
          : "Connect Google Workspace to enable delivery.",
      },
    },
    triggerHealth: {
      triggerKind: state.workflowRecord?.triggerConfig.kind ?? "manual",
      counter: 0,
      windowDescription:
        state.workflowRecord?.triggerConfig.kind === "schedule"
          ? "Daily"
          : "Manual trigger",
      lastTriggeredAt: state.workflowExecutions[0]?.createdAt ?? null,
      nextScheduledAt: null,
      autoDeliveryState: state.workflowRecord?.autonomousDelivery
        ? "enabled"
        : "manual_only",
    },
    canonicalThread: {
      proofRoute: "/school/admissions/dashboard",
      launchRoute: "/school/admissions/dashboard",
      launchQuestion: state.workflowRecord
        ? `Run the "${state.workflowRecord.title}" workflow.`
        : "Return to the admissions dashboard for canonical assistant proof.",
      latestExecutionId: state.workflowExecutions[0]?.id ?? null,
      latestExecutionStatus: state.workflowExecutions[0]?.status ?? null,
      latestRunId: state.workflowExecutions[0]?.runId ?? null,
    },
    defaultDraft: {
      title: `${DEFAULT_TEMPLATE.title} Draft`,
      routeScope: DEFAULT_TEMPLATE.routeScope,
      timezone: "America/New_York",
      triggerConfig: { kind: "manual" },
    },
  });

  const assistantWorkflowStudioAdapter: AssistantWorkflowStudioAdapter = {
    async getStudioState(input) {
      return buildWorkflowStudioState(input.workflowId);
    },
    async upsertDraft(input) {
      const now = Math.floor(Date.now() / 1000);
      const workflowId = String(
        input.workflowId ?? "assistant-workflow-storybook",
      );
      state.workflowRecord = {
        id: workflowId,
        templateId: input.templateId,
        title: input.title,
        description: input.description ?? null,
        creatorActorId: "storybook-staff",
        creatorActorRole: "staff",
        tenantId: "storybook-tenant",
        tenantScopeStatus: "scoped",
        module: "admissions",
        routeScope: input.routeScope,
        graphSpec: cloneValue(input.graphSpec),
        mermaidSpec:
          "flowchart LR; trigger-manual-->dataset-applications-->spreadsheet-output;",
        datasourceSelectionPlan: {
          module: "admissions",
          routeScope: input.routeScope,
          selectorSummary: "Admissions dataset",
          authorizedSources: ["applications"],
          filters: [],
          projections: ["studentName", "status"],
          groupBy: [],
          aggregates: [],
        },
        connectorBindings: cloneValue(input.connectorBindings ?? []),
        triggerConfig: cloneValue(input.triggerConfig),
        approvalStatus: "draft",
        autonomousDelivery: Boolean(input.autonomousDelivery),
        timezone: input.timezone,
        executionSnapshotHash: "storybook-execution-hash",
        compiledAslJson: '{"Comment":"storybook"}',
        compiledExecutionPackage: {},
        requiredOauthScopes: ["https://www.googleapis.com/auth/drive.file"],
        latestApproval: null,
        lastSubmittedAt: null,
        lastReviewedAt: null,
        lastCompiledAt: asUnixTimestamp(now),
        createdAt: state.workflowRecord?.createdAt ?? asUnixTimestamp(now),
        updatedAt: asUnixTimestamp(now),
      };
      return {
        workflow: cloneValue(state.workflowRecord),
        approval: null,
      } as IntellioAssistantWorkflowMutationResult;
    },
    async submitForApproval(input) {
      if (
        state.workflowRecord &&
        state.workflowRecord.id === input.workflowDefinitionId
      ) {
        state.workflowRecord = {
          ...state.workflowRecord,
          approvalStatus: "pending_approval",
          lastSubmittedAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
          updatedAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
        };
      }
      return {
        workflow: cloneValue(state.workflowRecord),
        approval: null,
      } as IntellioAssistantWorkflowMutationResult;
    },
    async reviewWorkflow(input) {
      if (
        state.workflowRecord &&
        state.workflowRecord.id === input.workflowDefinitionId
      ) {
        const approved = input.decision === "approved";
        state.workflowRecord = {
          ...state.workflowRecord,
          approvalStatus: approved ? "approved" : "rejected",
          autonomousDelivery: approved
            ? Boolean(input.autonomousDelivery)
            : false,
          lastReviewedAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
          updatedAt: asUnixTimestamp(Math.floor(Date.now() / 1000)),
        };
      }
      return {
        workflow: cloneValue(state.workflowRecord),
        approval: null,
      } as IntellioAssistantWorkflowMutationResult;
    },
    async mutateRun() {
      const createdAt = Math.floor(Date.now() / 1000);
      const execution: IntellioAssistantWorkflowExecutionRecord = {
        id: `assistant-workflow-execution-${createdAt}`,
        workflowDefinitionId:
          state.workflowRecord?.id ?? "assistant-workflow-storybook",
        creatorActorId: "storybook-staff",
        tenantId: "storybook-tenant",
        module: "admissions",
        route: "/school/admissions/dashboard",
        runId: `assistant-run-${createdAt}`,
        workflowKey: state.workflowRecord?.templateId ?? DEFAULT_TEMPLATE.id,
        triggerKind: state.workflowRecord?.triggerConfig.kind ?? "manual",
        triggerContext: null,
        status: "running",
        approvalRequired: false,
        executionSnapshotHash:
          state.workflowRecord?.executionSnapshotHash ??
          "storybook-execution-hash",
        stateMachineExecutionArn: null,
        blockedReason: null,
        resultSummary: "Workflow execution started from Storybook preview.",
        errorMessage: null,
        artifactRefs: [],
        createdAt: asUnixTimestamp(createdAt),
        startedAt: asUnixTimestamp(createdAt),
        completedAt: null,
        updatedAt: asUnixTimestamp(createdAt),
      };
      state.workflowExecutions = [execution, ...state.workflowExecutions];
      return {
        accepted: true,
        run: {
          id: execution.runId ?? `assistant-run-${createdAt}`,
          actorId: "storybook-staff",
          actorRole: "staff",
          tenantId: "storybook-tenant",
          tenantScopeStatus: "scoped",
          state: "queued",
          module: "settings",
          route:
            "/super-administrator/control-plane/assistant-workflows/storybook",
          question: "Run workflow",
          workItemId: null,
          workflowKey: execution.workflowKey,
          workflowDefinitionId: execution.workflowDefinitionId,
          workflowExecutionId: execution.id,
          executionSnapshotHash: execution.executionSnapshotHash,
          origin: null,
          traceId: null,
          answer: null,
          response: null,
          errorMessage: null,
          createdAt: asUnixTimestamp(createdAt),
          startedAt: null,
          completedAt: null,
          updatedAt: asUnixTimestamp(createdAt),
        },
      } as IntellioMutateAssistantRunOutput;
    },
  };

  const paymentControlPlaneAdapter: PaymentControlPlaneAdapter = {
    async getReadiness() {
      return cloneValue(state.paymentReadiness);
    },
    async updateControlPlane(input) {
      if (input.defaultStripeAccountId !== undefined) {
        state.paymentReadiness.controlPlane.defaultStripeAccountId =
          input.defaultStripeAccountId;
      }
      state.paymentReadiness.controlPlane.onboardingStatus =
        input.onboardingStatus ??
        state.paymentReadiness.controlPlane.onboardingStatus;
      state.paymentReadiness.controlPlane.updatedAt = new Date().toISOString();
      state.paymentReadiness.controlPlane.updatedBy = "storybook-seeded";
      state.paymentReadiness.publicTuition = {
        ...state.paymentReadiness.publicTuition,
        ...(input.publicTuition?.savedMethodsEnabled !== undefined
          ? { savedMethodsEnabled: input.publicTuition.savedMethodsEnabled }
          : {}),
        ...(input.publicTuition?.autoPayEnabled !== undefined
          ? { autoPayEnabled: input.publicTuition.autoPayEnabled }
          : {}),
        ...(input.publicTuition?.autoPaySweepKillSwitch !== undefined
          ? {
              autoPaySweepKillSwitch:
                input.publicTuition.autoPaySweepKillSwitch,
            }
          : {}),
        ...(input.publicTuition?.webhookReconciliationReady !== undefined
          ? {
              webhookReconciliationReady:
                input.publicTuition.webhookReconciliationReady,
            }
          : {}),
        ...(input.publicTuition?.transactionalEmailReady !== undefined
          ? {
              transactionalEmailReady:
                input.publicTuition.transactionalEmailReady,
            }
          : {}),
        ...(input.publicTuition?.launchQaApproved !== undefined
          ? { launchQaApproved: input.publicTuition.launchQaApproved }
          : {}),
        ...(input.publicTuition?.paymentMethodPolicy
          ? { paymentMethodPolicy: input.publicTuition.paymentMethodPolicy }
          : {}),
      };
      state.paymentReadiness.banquest = {
        ...state.paymentReadiness.banquest,
        ...(input.banquest?.enabled !== undefined
          ? { enabled: input.banquest.enabled }
          : {}),
        ...(input.banquest?.registrationMode
          ? { registrationMode: input.banquest.registrationMode }
          : {}),
        ...(input.banquest?.allowedImportModules
          ? { allowedImportModules: [...input.banquest.allowedImportModules] }
          : {}),
        ...(input.banquest?.webhookPathKey !== undefined
          ? { webhookPathKey: input.banquest.webhookPathKey }
          : {}),
        ...(input.banquest?.webhookId !== undefined
          ? { webhookId: input.banquest.webhookId }
          : {}),
        ...(input.banquest?.webhookActive !== undefined
          ? { webhookActive: input.banquest.webhookActive }
          : {}),
        ...(input.banquest?.lastWebhookSyncAt !== undefined
          ? { lastWebhookSyncAt: input.banquest.lastWebhookSyncAt }
          : {}),
      };
      if (
        state.paymentReadiness.banquest.enabled &&
        !state.paymentReadiness.banquest.webhookPathKey
      ) {
        state.paymentReadiness.banquest.webhookPathKey =
          "storybook-banquest-path";
      }
      state.paymentReadiness.banquest.webhookUrl = state.paymentReadiness
        .banquest.webhookPathKey
        ? `https://example.com/banquest/${state.paymentReadiness.banquest.webhookPathKey}`
        : null;
      const nextOneTimePrimary =
        input.donationsFunding?.oneTimePrimaryFundingMethod ??
        input.sola?.primaryDonationFundingMethod ??
        state.paymentReadiness.donationsFundingReadiness
          .oneTimePrimaryFundingMethod;
      const nextOneTimeFallback =
        input.donationsFunding?.oneTimeFallbackFundingMethod ??
        input.sola?.fallbackDonationFundingMethod ??
        state.paymentReadiness.donationsFundingReadiness
          .oneTimeFallbackFundingMethod;
      const nextRecurringFundingMethod =
        input.donationsFunding?.recurringFundingMethod ??
        state.paymentReadiness.donationsFundingReadiness.recurringFundingMethod;
      if (nextRecurringFundingMethod !== "stripe_card") {
        throw new Error(
          "Recurring donations currently require the Stripe card fallback.",
        );
      }
      state.paymentReadiness.donationsFundingReadiness = {
        ...state.paymentReadiness.donationsFundingReadiness,
        oneTimePrimaryFundingMethod: nextOneTimePrimary,
        oneTimeFallbackFundingMethod: nextOneTimeFallback,
        recurringFundingMethod: nextRecurringFundingMethod,
        oneTimeResolvedFundingMethod: nextOneTimePrimary,
        recurringResolvedFundingMethod: "stripe_card",
      };
      state.paymentReadiness.sola.publicIfieldsKey =
        input.sola?.publicIfieldsKey ??
        state.paymentReadiness.sola.publicIfieldsKey;
      state.paymentReadiness.sola.primaryDonationFundingMethod =
        nextOneTimePrimary;
      state.paymentReadiness.sola.fallbackDonationFundingMethod =
        nextOneTimeFallback;
      state.paymentReadiness.solaProviderReadiness.publicIfieldsKey =
        state.paymentReadiness.sola.publicIfieldsKey;
      state.paymentReadiness.solaProviderReadiness.hasPublicIfieldsKeyConfigured =
        Boolean(state.paymentReadiness.sola.publicIfieldsKey);
      if (
        input.schoolPaymentMethodConfigurationCatalog ||
        input.paymentMethodConfigurationAssignments
      ) {
        const currentPaymentMethodConfigurations =
          state.paymentReadiness.paymentMethodConfigurations ??
          buildPaymentMethodConfigurations();
        const nextSchoolPaymentMethodConfigurationCatalog =
          input.schoolPaymentMethodConfigurationCatalog
            ? cloneValue(input.schoolPaymentMethodConfigurationCatalog)
            : cloneValue(currentPaymentMethodConfigurations.schoolCatalog);
        const nextPaymentMethodConfigurationAssignments =
          input.paymentMethodConfigurationAssignments
            ? cloneValue(input.paymentMethodConfigurationAssignments)
            : assignmentsFromPaymentMethodConfigurations(
                currentPaymentMethodConfigurations,
              );
        state.paymentReadiness.paymentMethodConfigurations =
          buildPaymentMethodConfigurations(
            nextSchoolPaymentMethodConfigurationCatalog,
            nextPaymentMethodConfigurationAssignments,
          );
      }
      state.paymentReadiness.status =
        state.paymentReadiness.controlPlane.onboardingStatus === "active"
          ? "live_ready"
          : "test_ready";
      return {
        defaultStripeAccountId:
          state.paymentReadiness.controlPlane.defaultStripeAccountId,
        onboardingStatus: state.paymentReadiness.controlPlane.onboardingStatus,
        publicTuition: state.paymentReadiness.publicTuition,
        banquest: state.paymentReadiness.banquest,
        donationsFunding: {
          oneTimePrimaryFundingMethod:
            state.paymentReadiness.donationsFundingReadiness
              .oneTimePrimaryFundingMethod,
          oneTimeFallbackFundingMethod:
            state.paymentReadiness.donationsFundingReadiness
              .oneTimeFallbackFundingMethod,
          recurringFundingMethod:
            state.paymentReadiness.donationsFundingReadiness
              .recurringFundingMethod,
        },
      };
    },
    async startConnectedAccountOnboarding(input) {
      const stripeAccountId =
        input.existingStripeAccountId?.trim() || "acct_storybook_123";
      state.paymentReadiness.controlPlane.defaultStripeAccountId =
        stripeAccountId;
      state.paymentReadiness.controlPlane.onboardingStatus = "pending";
      return {
        action: input.existingStripeAccountId ? "linked" : "created",
        stripeAccountId,
        onboardingUrl: "https://connect.stripe.com/setup/s/storybook",
        expiresAt: Date.now() + 60_000,
        accountType: "express",
        contactEmail: input.contactEmail ?? null,
        country: input.country ?? "US",
      };
    },
    async completeConnectedAccountOnboarding(input) {
      state.paymentReadiness.controlPlane.defaultStripeAccountId =
        input.stripeAccountId ??
        state.paymentReadiness.controlPlane.defaultStripeAccountId;
      state.paymentReadiness.controlPlane.onboardingStatus = "active";
      state.paymentReadiness.status = "test_ready";
      return {
        defaultStripeAccountId:
          state.paymentReadiness.controlPlane.defaultStripeAccountId,
        onboardingStatus: state.paymentReadiness.controlPlane.onboardingStatus,
      };
    },
  };

  const financialAidHistoricalImportAdapter: FinancialAidHistoricalImportAdapter =
    {
      async getTemplate() {
        return {
          downloadUrl:
            "https://example.com/storybook-financial-aid-template.csv",
          templateVersion: "storybook-template-v1",
          fileName: "storybook-financial-aid-template.csv",
          expiresAt: Date.now() + 60_000,
        };
      },
      async getStatus() {
        return cloneValue(state.historicalImportStatus);
      },
      async requestUpload(input) {
        const importId = `storybook-import-${Date.now()}`;
        state.historicalImportStatus = {
          ...state.historicalImportStatus,
          status: "uploading",
          importId,
          lastFileName: input.fileName,
          lastS3Key: `storybook/${input.fileName}`,
        };
        return {
          importId,
          uploadUrl: "mock://staff-settings-upload/storybook.csv",
          s3Key: `storybook/${input.fileName}`,
          expiresAt: Date.now() + 60_000,
        };
      },
      async startImport(input) {
        state.historicalImportStatus = {
          ...state.historicalImportStatus,
          status: "completed",
          importId: input.importId ?? state.historicalImportStatus.importId,
          lastImportedAt: new Date().toISOString(),
          processedRows: 32,
          acceptedRows: 32,
          rejectedRows: 0,
          errorSummary: null,
        };
        return cloneValue(state.historicalImportStatus);
      },
    };

  const uiConsultationAdapter: UiConsultationAdapter = {
    visibility: state.uiConsultationStatus.visibility,
    async getServiceStatus() {
      return cloneValue(state.uiConsultationStatus);
    },
    async listSessions() {
      return cloneValue(state.uiConsultationSessions);
    },
    async getSession(input) {
      return cloneValue(
        state.uiConsultationSessions.find(
          (entry) => entry.id === input.sessionId,
        ) ?? null,
      );
    },
    async saveSession(input) {
      const now = new Date().toISOString();
      const id = String(input.id ?? `ui-consultation-session-${Date.now()}`);
      const session: UiConsultationSessionRecord = {
        id,
        title: String(input.title ?? "Untitled consultation"),
        consultationKind: input.consultationKind,
        targetAppId: input.targetAppId,
        targetRoute: input.targetRoute,
        targetPersona: input.targetPersona,
        targetPackageName: input.targetPackageName,
        targetComponentName: input.targetComponentName ?? null,
        question: input.question,
        latestRunId: input.latestRunId ?? null,
        latestStatus: input.latestStatus ?? null,
        updatedAt: now,
      };
      state.uiConsultationSessions = [
        session,
        ...state.uiConsultationSessions.filter((entry) => entry.id !== id),
      ];
      return cloneValue(session);
    },
    async listRuns(input) {
      return cloneValue(
        state.uiConsultationRuns.filter(
          (entry) => entry.sessionId === input.sessionId,
        ),
      );
    },
    async runConsultation(input) {
      const now = new Date().toISOString();
      const run: UiConsultationRunRecord = {
        id: `ui-consultation-run-${Date.now()}`,
        sessionId: input.session.id,
        status: "completed",
        consultationKind: input.request.consultationKind,
        targetRoute: input.request.target.route,
        question: input.request.question,
        summary:
          "Mock consultation completed from the shared settings package preview adapter.",
        configuredModelLabel: "Claude Opus 4.6",
        artifactDir: "artifacts/protocol-hub/opus-consultations/mock-preview",
        latestPointerPath:
          "artifacts/protocol-hub/opus-consultations/mock-preview/latest.json",
        request: cloneValue(input.request),
        response: {
          summary:
            "Mock consultation completed from the shared settings package preview adapter.",
          surfaceUnderstanding: [
            "Shared preview mode does not execute a live TailCodex consultation.",
          ],
          strengths: ["Route and adapter seams are wired."],
          failuresOrRisks: [
            "This is seeded preview data, not a live Bedrock response.",
          ],
          interactionRecommendations: [
            "Use the Intellio Demo app-backed adapter for real runs.",
          ],
          informationArchitectureRecommendations: [],
          visualHierarchyRecommendations: [],
          componentLevelChanges: [],
          dataBindingConcerns: [],
          acceptanceChecks: [
            "Verify the app-backed adapter before trusting results.",
          ],
          confidence: "low",
          citations: [],
        },
        rawResult: {
          status: "completed",
          source: "mockSettingsAdapters",
        },
        updatedAt: now,
      };
      state.uiConsultationRuns = [run, ...state.uiConsultationRuns];
      state.uiConsultationSessions = state.uiConsultationSessions.map(
        (entry) =>
          entry.id === input.session.id
            ? {
                ...entry,
                latestRunId: run.id,
                latestStatus: run.status,
                updatedAt: now,
              }
            : entry,
      );
      state.uiConsultationStatus = {
        ...state.uiConsultationStatus,
        latestRunId: run.id,
      };
      return cloneValue(run);
    },
  };

  return {
    settingsConfigAdapter,
    assistantSpreadsheetBridgeAdapter,
    assistantWorkflowStudioAdapter,
    paymentControlPlaneAdapter,
    financialAidHistoricalImportAdapter,
    uiConsultationAdapter,
  };
};
